// packages
import cx from 'classnames';
import { get, set } from '../vendor/idb-keyval';
import { h } from 'preact';

// components
import CandidateRow from './CandidateRow';
import Contest from './Contest';
import Portal from './Portal';
import PureComponent from './PureComponent';
import Typeahead from './Typeahead';

// internal
import debounce from '../utils/debounce';
import { determineContestTitleYourRaces } from '../election-utils';
import { lookup } from '../utils/legislative-districts';
import loadAssetURL from '../utils/load-asset-url';

const googleIMG = loadAssetURL('powered-by-google');

const sessionToken = new google.maps.places.AutocompleteSessionToken();
const autocompleteService = new google.maps.places.AutocompleteService();

const bounds = {
  east: -93.514764,
  west: -106.662177,
  south: 25.843902,
  north: 36.500199,
};

const componentRestrictions = { country: 'us' };
const types = ['geocode'];

const getPredictions = input => {
  return new Promise((resolve, reject) => {
    autocompleteService.getPlacePredictions(
      { bounds, componentRestrictions, input, sessionToken, types },
      (predict, status) => {
        if (status === 'OK') resolve(predict);

        reject(new Error('No results found.'));
      }
    );
  });
};

// always render what's provided, the geocoder decides
const shouldItemRender = () => true;

class YourRaces extends PureComponent {
  constructor() {
    super();

    this.state = {
      contests: [],
      coordinates: null,
      data: null,
      lookupError: false,
      lookupPending: false,
      lookedFoundStored: false,
      results: [],
      value: '',
    };

    this.handleGeocode = this.handleGeocode.bind(this);
    this.debouncedHandleGeocode = debounce(this.handleGeocode, 300);
    this.onChange = this.onChange.bind(this);
    this.onClear = this.onClear.bind(this);
    this.onSelect = this.onSelect.bind(this);
  }

  onClear(value) {
    this.setState({ value: '' });
  }

  onChange(value) {
    this.setState({ value }, this.debouncedHandleGeocode);
  }

  handleGeocode() {
    const { value: input } = this.state;

    getPredictions(input)
      .then(results => this.setState({ results }))
      .catch(() => this.setState({ results: [] }));
  }

  onSelect({ name, placeId }) {
    this.setState({ lookupError: false, lookupPending: true, value: name });

    this.placesService.getDetails(
      {
        fields: ['geometry.location'],
        placeId,
        sessionToken,
      },
      result => {
        const {
          lat: latitude,
          lng: longitude,
        } = result.geometry.location.toJSON();

        const coordinates = { latitude, longitude };

        lookup(coordinates)
          .then(({ data }) =>
            this.setState(
              {
                coordinates,
                data,
                lookupPending: false,
                lookedFoundStored: false,
              },
              () => set('your-races-lookup', data)
            )
          )
          .catch(() =>
            this.setState({
              data: null,
              lookupError: true,
              lookupPending: false,
            })
          );
      }
    );
  }

  componentDidMount() {
    this.placesService = new google.maps.places.PlacesService(this.inputRef);

    get('your-races-lookup').then(data => {
      if (data != null) {
        this.setState({ data, lookedFoundStored: true });
      }
    });
  }

  render() {
    const { contests, flags, annotations, asOf } = this.props;
    const {
      coordinates,
      data,
      lookupError,
      lookupPending,
      lookedFoundStored,
      results,
      value,
    } = this.state;

    let addressFeedback = null;

    const matches = {
      'us-house': null,
      sboe: null,
      'tx-senate': null,
      'tx-house': null,
    };

    const flagMatches = {
      'us-house': null,
      sboe: null,
      'tx-senate': null,
      'tx-house': null,
    };

    const annoMatches = {
      'us-house': null,
      sboe: null,
      'tx-senate': null,
      'tx-house': null,
    };

    if (data) {
      matches['us-house'] = contests.filter(
        contest =>
          contest.office === 'U.S. House' &&
          contest.office_division === data.us_house.district
      );

      matches['sboe'] = contests.filter(
        contest =>
          contest.office === 'State Board of Education' &&
          contest.office_division === data.sboe.district
      );

      matches['tx-senate'] = contests.filter(
        contest =>
          contest.office === 'Texas Senate' &&
          contest.office_division === data.tx_senate.district
      );

      matches['tx-house'] = contests.filter(
        contest =>
          contest.office === 'Texas House' &&
          contest.office_division === data.tx_house.district
      );

      flagMatches['us-house'] = flags.filter(
        contest =>
          contest.type === 'us-house-district-' + data.us_house.district
      );

      flagMatches['sboe'] = flags.filter(
        contest =>
          contest.type ===
          'state-board-of-education-district-' + data.sboe.district
      );

      flagMatches['tx-senate'] = flags.filter(
        contest =>
          contest.type === 'texas-senate-district-' + data.tx_senate.district
      );

      flagMatches['tx-house'] = flags.filter(
        contest =>
          contest.type === 'texas-house-district-' + data.tx_house.district
      );

      annoMatches['us-house'] = annotations.filter(
        contest =>
          contest.type === 'us-house-district-' + data.us_house.district
      );

      annoMatches['sboe'] = annotations.filter(
        contest =>
          contest.type ===
          'state-board-of-education-district-' + data.sboe.district
      );

      annoMatches['tx-senate'] = annotations.filter(
        contest =>
          contest.type === 'texas-senate-district-' + data.tx_senate.district
      );

      annoMatches['tx-house'] = annotations.filter(
        contest =>
          contest.type === 'texas-house-district-' + data.tx_house.district
      );
    }

    if (lookupPending) {
      addressFeedback = (
        <p class="has-padding t-serif t-weight-bold t-align-center">
          Finding your details...{' '}
          <svg class="icon icon-spinner animate-pulse">
            <use xlinkHref="#icon-spinner" />
          </svg>
        </p>
      );
    }

    if (lookupError) {
      addressFeedback = (
        <p class="has-padding t-serif t-weight-bold t-align-center">
          Hmm... we were unable to find you in Texas. Please check your address
          and try again!
        </p>
      );
    }

    if (coordinates && !lookupPending && !lookupError) {
      addressFeedback = (
        <p class="has-padding t-serif t-weight-bold t-align-center">
          We found your information! These races have been personalized for you.
        </p>
      );
    }

    if (lookedFoundStored && !lookupPending && !lookupError) {
      addressFeedback = (
        <p class="has-padding t-serif t-weight-bold t-align-center">
          Welcome back! We remembered the districts you found last time.
        </p>
      );
    }

    const inputProps = {
      id: 'address-input',
      class: 'address-input__input',
      placeholder: '1010 Colorado St, Austin, TX, 78701',
    };

    return (
      <div class="address-input t-sans">
        <svg class="icon icon-drawn-left-arrow">
          <use xlinkHref="#icon-drawn-left-arrow"></use>
        </svg>
        <label class="address-input__label" for="address-input">
          Enter your address
        </label>
        <Typeahead
          inputProps={inputProps}
          items={results}
          getItemValue={item => ({
            name: item.description,
            placeId: item.place_id,
          })}
          value={value}
          onChange={this.onChange}
          onClear={this.onClear}
          onSelect={this.onSelect}
          renderItem={(item, isHighlighted) => (
            <li
              class={cx('listbox__item', {
                'listbox__item--highlighted': isHighlighted,
              })}
              key={item.id}
            >
              {item.description}
            </li>
          )}
          renderAttribution={() => (
            <li class="listbox__item listbox__item--attribution">
              <img src={googleIMG} alt="Powered by Google" />
            </li>
          )}
          shouldItemRender={shouldItemRender}
          renderInput={props => (
            <input
              {...props}
              ref={el => {
                props.ref(el);
                this.inputRef = el;
              }}
            />
          )}
        />
        <Portal node={document.getElementById('your-address-feedback')}>
          {addressFeedback}
        </Portal>
        {data && contests.length > 0 && (
          <Portal
            node={document.getElementById('your-races-results')}
            ref={ref => (this._resultsPortal = ref)}
          >
            <div class="representative-districts contest-list seat__contests-double">
              <Contest
                key={matches['us-house'][0].office_slug}
                contest={matches['us-house'][0]}
                flags={flagMatches['us-house']}
                annotations={annoMatches['us-house']}
                contestTitle={determineContestTitleYourRaces(
                  matches['us-house'][0]
                )}
                asOf={asOf}
                tableHeader={CandidateRow.Header}
                columnCount={CandidateRow.columnCount}
                renderCandidate={(
                  candidate,
                  idx,
                  viewAllCandidates,
                  { totalVotes }
                ) => (
                  <CandidateRow
                    key={candidate.id}
                    ballotName={candidate.name}
                    candidateId={candidate.id}
                    contestIsCalled={matches['us-house'].is_called}
                    isIncumbent={candidate.is_incumbent}
                    isWinner={candidate.is_winner}
                    isViewable={viewAllCandidates || idx < 3}
                    party={candidate.party}
                    totalVotesInContest={totalVotes}
                    votes={candidate.votes}
                  />
                )}
              />
              {matches['sboe'].length > 0 && (
                <Contest
                  key={matches['sboe'][0].office_slug}
                  contest={matches['sboe'][0]}
                  flags={flagMatches['sboe']}
                  annotations={annoMatches['sboe']}
                  contestTitle={determineContestTitleYourRaces(
                    matches['sboe'][0]
                  )}
                  asOf={asOf}
                  tableHeader={CandidateRow.Header}
                  columnCount={CandidateRow.columnCount}
                  renderCandidate={(
                    candidate,
                    idx,
                    viewAllCandidates,
                    { totalVotes }
                  ) => (
                    <CandidateRow
                      key={candidate.id}
                      ballotName={candidate.name}
                      candidateId={candidate.id}
                      contestIsCalled={matches['sboe'].is_called}
                      isIncumbent={candidate.is_incumbent}
                      isWinner={candidate.is_winner}
                      isViewable={viewAllCandidates || idx < 3}
                      party={candidate.party}
                      totalVotesInContest={totalVotes}
                      votes={candidate.votes}
                    />
                  )}
                />
              )}
              {matches['tx-senate'].length > 0 && (
                <Contest
                  key={matches['tx-senate'][0].office_slug}
                  contest={matches['tx-senate'][0]}
                  flags={flagMatches['tx-senate']}
                  annotations={annoMatches['tx-senate']}
                  columnCount={CandidateRow.columnCount}
                  contestTitle={determineContestTitleYourRaces(
                    matches['tx-senate'][0]
                  )}
                  asOf={asOf}
                  tableHeader={CandidateRow.Header}
                  columnCount={CandidateRow.columnCount}
                  renderCandidate={(candidate, { totalVotes }) => (
                    <CandidateRow
                      key={candidate.id}
                      ballotName={candidate.name}
                      candidateId={candidate.id}
                      contestIsCalled={matches['tx-senate'].is_called}
                      isIncumbent={candidate.is_incumbent}
                      isWinner={candidate.is_winner}
                      party={candidate.party}
                      totalVotesInContest={totalVotes}
                      votes={candidate.votes}
                    />
                  )}
                />
              )}
              <Contest
                key={matches['tx-house'][0].office_slug}
                contest={matches['tx-house'][0]}
                flags={flagMatches['tx-house']}
                annotations={annoMatches['tx-house']}
                columnCount={CandidateRow.columnCount}
                contestTitle={determineContestTitleYourRaces(
                  matches['tx-house'][0]
                )}
                asOf={asOf}
                tableHeader={CandidateRow.Header}
                columnCount={CandidateRow.columnCount}
                renderCandidate={(
                  candidate,
                  idx,
                  viewAllCandidates,
                  { totalVotes }
                ) => (
                  <CandidateRow
                    key={candidate.id}
                    ballotName={candidate.name}
                    candidateId={candidate.id}
                    contestIsCalled={matches['tx-house'].is_called}
                    isIncumbent={candidate.is_incumbent}
                    isViewable={viewAllCandidates || idx < 3}
                    isWinner={candidate.is_winner}
                    party={candidate.party}
                    totalVotesInContest={totalVotes}
                    votes={candidate.votes}
                  />
                )}
              />
            </div>
          </Portal>
        )}
      </div>
    );
  }
}

export default YourRaces;
